import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import { sendOnWhatsApp, buildGalleryMessage, buildEventInviteMessage } from '../../utils/sendOnWhatsApp';

const API = '/api/superadmin';
const SHARE_BASE = process.env.REACT_APP_SHARE_BASE || process.env.REACT_APP_API_URL || (window.location.origin + '/em');
const PHOTO_BASE = process.env.REACT_APP_PHOTO_BASE || '';
const IS_LOCALHOST = SHARE_BASE.includes('localhost') || SHARE_BASE.includes('127.0.0.1');
const copyText = txt => navigator.clipboard?.writeText(txt).catch(() => {});

const lowerExt = (name) => {
  if (!name) return name;
  return name
    .replace(/\.[A-Z][A-Za-z0-9]*(\.[a-z][a-z0-9]*)$/, '$1')
    .replace(/\.[^/.]+$/, e => e.toLowerCase());
};

const FIELDS = [
  ['Surname',        'surname'],
  ['Name',           'name'],
  ['Mobile',         'mobile_number', 'tel'],
  ['Email',          'email',         'email'],
  ['Password',       'password',      'password'],
  ['Father/Husband', 'father_husband'],
  ['Date of Birth',  'dob',           'date'],
  ['Address 1',      'address1'],
  ['Address 2',      'address2'],
  ['Pincode',        'pincode'],
  ['District',       'district'],
  ['State',          'state'],
  ['Spouse Name',   'spounce_name'],
  ['Spouse Mobile', 'spounce_mobile', 'tel'],
  ['Referred By',   'refered_by'],
  ['ISD',            'isd'],
];

const COLORS = ['bg-blue-700','bg-purple-700','bg-green-700','bg-amber-700','bg-rose-700','bg-teal-700','bg-indigo-700'];

// ── Selfie-or-Avatar ─────────────────────────────────────────────────────────────
const SelfieOrAvatar = ({ userId, name, colorClass = 'bg-blue-700', size = 'w-8 h-8', bust = '' }) => {
  const [failed, setFailed] = useState(false);
  const ini = (name || '?').split(' ').filter(Boolean).map(w => w[0]).join('').slice(0, 2).toUpperCase();
  useEffect(() => { setFailed(false); }, [userId, bust]);
  if (failed) return (
    <div className={`${size} rounded-full flex items-center justify-center font-bold text-[10px] text-white flex-shrink-0 ${colorClass}`}>{ini}</div>
  );
  return (
    <div className={`${size} rounded-full overflow-hidden flex-shrink-0 border-2 border-white shadow-sm`}>
      <img
        src={`/api/photos/selfie/${userId}${bust ? `?t=${bust}` : ''}`}
        alt={name}
        className="w-full h-full object-cover"
        loading="lazy"
        onError={() => setFailed(true)}
      />
    </div>
  );
};

// ── Sub-components ───────────────────────────────────────────────────────────────

// ── Main component ──────────────────────────────────────────────────────────────
const SuperAdminEventOwnersPage = () => {
  const token   = localStorage.getItem('ep_token');
  const headers = { Authorization: `Bearer ${token}` };

  // ── Owner section ────────────────────────────────────────────────────────────
  const [dealers,       setDealers]       = useState([]);
  const [dealerId,      setDealerId]      = useState('');
  const [photographers,  setPhotographers]  = useState([]);
  const [photogId,      setPhotogId]       = useState('');
  const [events,        setEvents]         = useState([]);
  const [eventId,       setEventId]        = useState('');
  const [owner,         setOwner]          = useState(null);
  const [form,          setForm]           = useState(null);
  const [saving,        setSaving]         = useState(false);
  const [msg,           setMsg]            = useState('');
  const [, setLoadingP]        = useState(false);
  const [, setLoadingE]        = useState(false);

  // ── Visitors section ────────────────────────────────────────────────────────
  const [visitors,        setVisitors]        = useState([]);
  const [loadingVis,      setLoadingVis]      = useState(false);
  const [selected,        setSelected]        = useState(null);
  const [rightView,       setRightView]       = useState(null); // 'photos' | 'edit'
  const [search,          setSearch]          = useState('');
  const [selfieBust,      setSelfieBust]      = useState({});
  const [selfieFile,      setSelfieFile]      = useState(null);
  const [uploadingSelfie, setUploadingSelfie] = useState(false);
  const [selfieMsg,       setSelfieMsg]       = useState('');

  // Photos
  const [photos,         setPhotos]         = useState([]);
  const [loadingPhotos,   setLoadingPhotos]   = useState(false);
  const [photosError,     setPhotosError]     = useState('');
  const [lightbox,        setLightbox]        = useState(null);

  // WhatsApp
  const [waSending, setWaSending] = useState('');
  const [waResult,  setWaResult]  = useState('');
  const [copied,    setCopied]    = useState('');

  // Event details modal
  const [showEventModal, setShowEventModal] = useState(false);

  // ── Owner loaders ───────────────────────────────────────────────────────────
  useEffect(() => {
    axios.get(`${API}/users/dealers`, { headers }).then(r => setDealers(r.data)).catch(() => {});
  }, []);

  const loadPhotographers = useCallback(async (dId) => {
    setLoadingP(true);
    try { const { data } = await axios.get(`${API}/users/photographers?dealer_id=${dId}`, { headers }); setPhotographers(data); }
    catch { setPhotographers([]); }
    setLoadingP(false);
  }, []);

  const loadEvents = useCallback(async (pId) => {
    setLoadingE(true);
    try { const { data } = await axios.get(`${API}/photographers/${pId}/events`, { headers }); setEvents(data); }
    catch { setEvents([]); }
    setLoadingE(false);
  }, []);

  // ── Cascade handlers ─────────────────────────────────────────────────────────
  const handleDealerChange = (id) => {
    setDealerId(id); setPhotogId(''); setPhotographers([]); setEventId(''); setEvents([]);
    setOwner(null); setForm(null); setMsg('');
    setVisitors([]); setSelected(null); setRightView(null);
    if (id) loadPhotographers(id);
  };

  const handlePhotogChange = (id) => {
    setPhotogId(id); setEventId(''); setEvents([]);
    setOwner(null); setForm(null); setMsg('');
    setVisitors([]); setSelected(null); setRightView(null);
    if (id) loadEvents(id);
  };

  const handleEventChange = (id) => {
    setEventId(id); setOwner(null); setForm(null); setMsg('');
    setSelected(null); setRightView(null);
    if (!id) return;
    const ev = events.find(e => String(e.id) === String(id));
    if (ev?.owner_id) {
      axios.get(`${API}/users/${ev.owner_id}`, { headers }).then(r => {
        setOwner(r.data); setForm(toForm(r.data));
      }).catch(() => {
        setOwner({ id: ev.owner_id, Name: ev.owner_name, mobile_number: ev.owner_mobile });
        setForm(toForm({ Name: ev.owner_name, mobile_number: ev.owner_mobile }));
      });
    }
    // Load visitors
    loadVisitorsForEvent(id);
  };

  const loadVisitorsForEvent = useCallback(async (eId) => {
    setLoadingVis(true);
    try {
      const { data } = await axios.get(`${API}/investigate/events/${eId}/visitors`, { headers });
      setVisitors(data);
    } catch { setVisitors([]); }
    setLoadingVis(false);
  }, []);

  // ── Owner helpers ────────────────────────────────────────────────────────────
  const toForm = (o) => ({
    surname:        o.Surname        || o.surname        || '',
    name:           o.Name           || o.name           || '',
    mobile_number:  o.MobileNumber   || o.mobile_number  || '',
    email:          o.Email          || o.email          || '',
    password:       '',
    father_husband: o.FatherHusband  || o.father_husband || '',
    dob:            (o.Dob || o.dob) ? String(o.Dob || o.dob).split('T')[0] : '',
    address1:       o.Address1       || o.address1       || '',
    address2:       o.Address2       || o.address2       || '',
    pincode:        o.Pincode        || o.pincode        || '',
    district:       o.District       || o.district       || '',
    state:          o.State          || o.state          || '',
    spounce_name:   o.SpounceName    || o.spounce_name   || '',
    spounce_mobile: o.SpounceMobile  || o.spounce_mobile || '',
    refered_by:     o.ReferedBy      || o.refered_by     || '',
    isd:            o.ISD            || o.isd            || '91',
    is_active:      o.IsActive === 1 || o.is_active === 1 || o.IsActive === true || o.is_active === true,
  });

  const saveOwner = async () => {
    if (!owner) return;
    setSaving(true); setMsg('');
    try {
      const uid = owner.id || owner.UsersId;
      const payload = { ...form, role: 'event_owner' };
      if (!payload.password) delete payload.password;
      await axios.put(`${API}/users/${uid}`, payload, { headers });
      setMsg('Saved successfully.');
    } catch (err) { setMsg(err.response?.data?.error || 'Save failed.'); }
    setSaving(false);
  };

  // ── Visitor helpers ──────────────────────────────────────────────────────────
  const selectVisitor = (v, view = 'photos') => {
    setSelected(v); setRightView(view); setPhotos([]); setPhotosError(''); setSelfieMsg('');
  };
  const switchView = v => { setRightView(v); if (v === 'photos') setPhotos([]); setPhotosError(''); };
  const closeRight = () => { setSelected(null); setRightView(null); setPhotos([]); setPhotosError(''); };

  const visitorDisplayName = selected
    ? `${form?.surname || selected.Surname || ''} ${form?.name || selected.Name || ''}`.trim()
    : '';

  // Compute form for selected visitor (edit view) — stored in selFormState

  // ── Load photos ──────────────────────────────────────────────────────────────
  const loadPhotos = useCallback(async (eumId, galleryToken) => {
    setLoadingPhotos(true); setPhotos([]); setPhotosError('');
    try {
      if (galleryToken) {
        const { data } = await axios.get(`/api/gallery/token/${galleryToken}`);
        const list = (data.photos || []).map(p => ({
          id: p.id,
          file_name: p.file_name || '',
          confidence_score: p.confidence_percent != null ? p.confidence_percent / 100 : (p.confidence_score || 0),
          photoUrl: p.url,
        }));
        if (list.length > 0) { setPhotos(list); setLoadingPhotos(false); return; }
      }
      if (eumId) {
        const { data } = await axios.get(`${API}/investigate/visitors/${eumId}/matched-photos?limit=500`, { headers });
        const list = (data.photos || []).map(p => ({
          ...p,
          photoUrl: (PHOTO_BASE && p.event_id && p.file_name)
            ? `${PHOTO_BASE}/${p.event_id}/${lowerExt(p.file_name)}`
            : `/api/photos/file/${p.photo_token}`,
          confidence_score: p.confidence_score || 0,
        }));
        setPhotos(list);
      } else {
        setPhotosError('EUM_ID missing — please restart the Node.js server and reload.');
      }
    } catch (err) {
      setPhotosError(`Failed to load photos: ${err.response?.data?.error || err.message || 'Unknown error'}`);
    }
    setLoadingPhotos(false);
  }, []);

  useEffect(() => {
    if (selected && rightView === 'photos') loadPhotos(selected.eum_id || selected.id, selected.gallery_token);
  }, [selected, rightView]);

  // ── Save visitor ──────────────────────────────────────────────────────────────
  const [visitorSaveMsg, setVisitorSaveMsg] = useState('');
  const [visitorSaving,  setVisitorSaving]  = useState(false);
  const saveVisitor = async () => {
    if (!selected) return;
    setVisitorSaving(true); setVisitorSaveMsg('');
    try {
      const uid = selected.id;
      const payload = { ...selFormState, role: 'visitor' };
      if (!payload.password) delete payload.password;
      await axios.put(`${API}/users/${uid}`, payload, { headers });
      setVisitorSaveMsg('Saved successfully.');
      loadVisitorsForEvent(eventId);
    } catch (err) { setVisitorSaveMsg(err.response?.data?.error || 'Save failed.'); }
    setVisitorSaving(false);
  };

  // ── Selfie upload ────────────────────────────────────────────────────────────
  const uploadSelfie = async () => {
    if (!selfieFile || !selected?.id) return;
    setUploadingSelfie(true); setSelfieMsg('');
    try {
      const fd = new FormData();
      fd.append('selfie', selfieFile);
      await axios.post(`${API}/investigate/visitors/${selected.eum_id || selected.id}/replace-selfie`, fd, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } });
      setSelfieMsg('✓ Selfie uploaded — embedding recomputes in ~15 sec');
      setSelfieFile(null);
      setSelfieBust(prev => ({ ...prev, [selected.id]: Date.now() }));
    } catch (err) { setSelfieMsg(err.response?.data?.error || 'Upload failed.'); }
    setUploadingSelfie(false);
  };

  // ── Gallery token generate ───────────────────────────────────────────────────
  const [generatingLink, setGeneratingLink] = useState(false);
  const generateGalleryLink = async () => {
    if (!selected?.eum_id) return;
    setGeneratingLink(true);
    try {
      const { data } = await axios.post(`${API}/investigate/visitors/${selected.eum_id}/generate-gallery`, {}, { headers });
      const updated = { ...selected, gallery_token: data.gallery_token };
      setSelected(updated);
      setVisitors(prev => prev.map(v => v.id === selected.id ? updated : v));
    } catch {}
    setGeneratingLink(false);
  };

  // ── WhatsApp ─────────────────────────────────────────────────────────────────
  const sendWA = async (which, mobileRaw, message) => {
    setWaSending(which); setWaResult('');
    try { await sendOnWhatsApp({ mobileRaw, message, token }); setWaResult('ok'); }
    catch { setWaResult('fail'); }
    setWaSending('');
    setTimeout(() => setWaResult(''), 3000);
  };

  // ── Computed ─────────────────────────────────────────────────────────────────
  const filteredVisitors = useMemo(() => {
    if (!search) return visitors;
    const q = search.toLowerCase();
    return visitors.filter(v =>
      (v.Name    || '').toLowerCase().includes(q) ||
      (v.Surname || '').toLowerCase().includes(q) ||
      (v.mobile_number || '').includes(q)
    );
  }, [visitors, search]);

  const selectedEvent   = events.find(e => String(e.id) === String(eventId));
  const galleryUrl      = selected?.gallery_token ? `${SHARE_BASE}/gallery?token=${selected.gallery_token}` : null;
  const eventRegisterUrl = eventId && selectedEvent?.register_token
    ? `${SHARE_BASE}/register?token=${selectedEvent.register_token}`
    : eventId ? `${SHARE_BASE}/register?event_id=${eventId}` : null;
  const ownerName       = owner ? `${form?.surname || ''} ${form?.name || ''}`.trim() : '';
  const f                = (k, v) => setForm(p => ({ ...p, [k]: v }));

  // selForm setter (mutable-safe)
  const [selFormState, setSelFormState] = useState(null);
  useEffect(() => {
    if (selected) setSelFormState(toForm(selected));
  }, [selected]);

  const setSelForm = (updater) => {
    setSelFormState(prev => typeof updater === 'function' ? updater(prev) : updater);
  };

  // Escape key
  const escRef = useRef(null);
  escRef.current = () => {
    if (rightView === 'edit' || rightView === 'photos') { closeRight(); return; }
    if (eventId)  { handleEventChange(''); return; }
    if (photogId) { handlePhotogChange(''); return; }
    if (dealerId) { handleDealerChange(''); return; }
  };
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') escRef.current?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col h-screen overflow-hidden" style={{ paddingTop: 48 }}>
      <div className="flex flex-col flex-1 overflow-hidden p-2 gap-1.5">

        {/* ── Header filter bar ── */}
        <div className="flex items-center gap-2 bg-white rounded-xl border border-slate-200 px-3 py-1.5 flex-shrink-0">
          <span className="text-xs font-extrabold text-slate-700 flex-shrink-0">🎭 Event Owners</span>
          <span className="text-slate-300 text-sm flex-shrink-0">›</span>
          {[
            { val: dealerId,  onChange: handleDealerChange,  opts: dealers,      lbl: 'Dealer',    dis: false },
            { val: photogId,  onChange: handlePhotogChange,  opts: photographers, lbl: 'Photog',    dis: !dealerId },
            { val: eventId,   onChange: handleEventChange,   opts: events,        lbl: 'Event',     dis: !photogId },
          ].map(({ val, onChange, opts, lbl, dis }, i) => (
            <div key={lbl} className="flex items-center gap-1">
              {i > 0 && <span className="text-slate-300 text-sm flex-shrink-0">›</span>}
              <select className="border border-slate-200 rounded px-1.5 py-0.5 text-xs outline-none focus:border-indigo-400 w-32" value={val} onChange={e => onChange(e.target.value)} disabled={dis}>
                <option value="">— {lbl} —</option>
                {opts.map(o => (
                  <option key={o.id} value={o.id}>
                    {lbl === 'Dealer' ? (o.Surname ? `${o.Surname} ${o.Name}` : o.Name) :
                     lbl === 'Photog' ? (o.Surname ? `${o.Surname} ${o.Name}` : o.Name) :
                     o.name || o.Name}
                  </option>
                ))}
              </select>
            </div>
          ))}

          {/* Action buttons */}
          {eventId && (
            <div className="ml-auto flex items-center gap-1.5 flex-shrink-0">
              {visitors.length > 0 && (
                <span className="text-[10px] font-semibold text-indigo-600 bg-indigo-50 border border-indigo-200 rounded-full px-2 py-0.5">{visitors.length} visitors</span>
              )}
              <button onClick={() => setShowEventModal(true)} className="text-[10px] font-bold bg-slate-100 hover:bg-amber-100 text-amber-700 px-2 py-1 rounded-lg border border-slate-200">✏️ Event Details</button>
            </div>
          )}
        </div>

        {/* ── Main content ── */}
        {!eventId && (
          <div className="flex-1 flex items-center justify-center text-slate-400 text-xs">
            Select Dealer → Photographer → Event to manage the event
          </div>
        )}

        {eventId && (
          <div className="flex flex-1 overflow-hidden gap-2 min-h-0">

            {/* ══════════════════════════════════════════════════════════
                LEFT: Owner form + Visitor list (stacked)
            ══════════════════════════════════════════════════════════ */}
            <div className="flex flex-col gap-1.5 w-72 flex-shrink-0 overflow-hidden">

              {/* Owner form */}
              {owner && form && (
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden flex-shrink-0">
                  <div className="flex items-center gap-2 px-2.5 py-1.5 border-b border-slate-100 bg-slate-50">
                    <div className="w-6 h-6 rounded-full bg-amber-700 text-white flex items-center justify-center font-bold text-[9px] flex-shrink-0">
                      {(ownerName || '?').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-xs text-slate-800 truncate">{ownerName}</div>
                      <div className="text-[9px] text-slate-400">Event Owner</div>
                    </div>
                    {msg && (
                      <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-lg border ${msg.includes('uccess') ? 'bg-green-50 border-green-200 text-green-700' : 'bg-red-50 border-red-200 text-red-700'}`}>
                        {msg.includes('uccess') ? '✓' : '✗'}
                      </span>
                    )}
                  </div>
                  <div className="p-2 overflow-y-auto max-h-36">
                    <div className="grid grid-cols-2 gap-x-2 gap-y-1">
                      {['surname','name','mobile_number','email'].map(key => (
                        <div key={key} className="flex flex-col gap-0.5">
                          <label className="text-[8px] font-bold text-slate-400 uppercase">{key.replace('_',' ')}</label>
                          <input
                            className="border border-slate-200 rounded px-1.5 py-0.5 text-[10px] outline-none focus:border-indigo-400 w-full"
                            type={key === 'email' ? 'email' : key === 'mobile_number' ? 'tel' : 'text'}
                            value={form[key] || ''}
                            onChange={e => f(key, e.target.value)}
                          />
                        </div>
                      ))}
                    </div>
                    <div className="flex items-center justify-between mt-1.5">
                      <label className="flex items-center gap-1 text-[10px] font-semibold text-slate-600 cursor-pointer">
                        <input type="checkbox" checked={!!form.is_active} onChange={e => f('is_active', e.target.checked)} className="w-3 h-3" />
                        Active
                      </label>
                      <button onClick={saveOwner} disabled={saving} className="text-[10px] font-bold bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white px-2.5 py-0.5 rounded-lg">
                        {saving ? '…' : 'Save'}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Visitor list */}
              <div className="flex-1 bg-white rounded-xl border border-slate-200 flex flex-col overflow-hidden min-h-0">
                <div className="px-2 pt-1.5 pb-1 flex-shrink-0 border-b border-slate-100">
                  <input
                    className="w-full border border-slate-200 rounded px-2 py-1 text-[10px] outline-none focus:border-indigo-400"
                    placeholder="🔍 Search visitor…"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                  />
                </div>
                {loadingVis ? (
                  <div className="text-[10px] text-slate-400 px-2 py-2">Loading…</div>
                ) : visitors.length === 0 ? (
                  <div className="text-[10px] text-slate-400 px-2 py-2">No visitors registered.</div>
                ) : (
                  <div className="flex-1 overflow-y-auto p-1">
                    {filteredVisitors.map((v, i) => {
                      const isActive = selected?.id === v.id;
                      const fullName = `${v.Surname || ''} ${v.Name || ''}`.trim() || '?';
                      return (
                        <div key={v.id} className={`flex items-center gap-1.5 px-1.5 py-1.5 rounded-lg border mb-0.5 transition-colors cursor-pointer ${isActive ? 'bg-indigo-50 border-indigo-200' : 'border-transparent hover:bg-slate-50 hover:border-slate-200'}`}>
                          {/* Selfie thumb */}
                          <div onClick={() => selectVisitor(v, 'photos')}>
                            <SelfieOrAvatar userId={v.id} name={fullName} colorClass={COLORS[i % COLORS.length]} size="w-7 h-7" bust={selfieBust[v.id]} />
                          </div>
                          {/* Info */}
                          <div className="flex-1 min-w-0" onClick={() => selectVisitor(v, 'photos')}>
                            <div className="font-semibold text-[10px] text-slate-800 truncate leading-tight">{fullName}</div>
                            <div className="text-[9px] text-slate-400 truncate">{v.mobile_number}</div>
                            <div className="flex items-center gap-1 mt-0.5">
                              <span className="text-[8px] font-mono text-slate-400">EUM:{v.id}</span>
                              <span className="text-[8px] font-mono text-slate-400">UID:{v.user_id}</span>
                            </div>
                            {v.match_count > 0 && <div className="text-[9px] text-green-700 font-semibold">{v.match_count}📷</div>}
                            {!v.gallery_token && <div className="text-[9px] text-amber-600 font-semibold">⚠ no token</div>}
                          </div>
                          {/* Edit */}
                          <button
                            onClick={() => selectVisitor(v, 'edit')}
                            className={`text-[9px] font-bold px-1 py-0.5 rounded border flex-shrink-0 ${isActive && rightView === 'edit' ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-slate-50 text-slate-500 border-slate-200 hover:bg-slate-100'}`}
                          >✏️</button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* ══════════════════════════════════════════════════════════
                RIGHT: Visitor detail OR Event-level panel
            ══════════════════════════════════════════════════════════ */}
            {!selected ? (
              <EventLevelPanel
                eventId={eventId}
                eventName={selectedEvent?.name || ''}
                visitorCount={visitors.length}
                token={token}
                headers={headers}
                eventRegisterUrl={eventRegisterUrl}
              />
            ) : (
              <div className="flex-1 bg-white rounded-xl border border-slate-200 flex flex-col overflow-hidden min-w-0">

                {/* Header */}
                <div className="flex items-center gap-2 px-3 py-1.5 border-b border-slate-100 bg-slate-50 flex-shrink-0">
                  <SelfieOrAvatar userId={selected.id} name={visitorDisplayName} colorClass="bg-blue-700" size="w-8 h-8" bust={selfieBust[selected.id]} />
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-xs text-slate-800 truncate">{visitorDisplayName || '—'}</div>
                    <div className="text-[9px] text-slate-400 truncate">{selected.mobile_number}</div>
                  </div>
                  <button onClick={() => switchView('photos')} className={`text-[10px] font-semibold px-2 py-1 rounded-lg border ${rightView === 'photos' ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-600 border-slate-200'}`}>
                    📷 {selected.match_count || 0} Photos
                  </button>
                  <button onClick={() => switchView('edit')} className={`text-[10px] font-semibold px-2 py-1 rounded-lg border ${rightView === 'edit' ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-600 border-slate-200'}`}>
                    ✏️ Edit
                  </button>
                  <button onClick={closeRight} className="w-6 h-6 flex items-center justify-center rounded-full hover:bg-slate-200 text-slate-400 font-bold text-xs flex-shrink-0">✕</button>
                </div>

                {/* Gallery bar */}
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1 px-3 py-1.5 bg-indigo-50 border-b border-indigo-100 flex-shrink-0">
                  <span className="text-[9px] font-bold text-indigo-600 uppercase flex-shrink-0">Gallery</span>
                  {IS_LOCALHOST && (
                    <span className="text-[9px] text-amber-700 bg-amber-100 border border-amber-300 rounded px-1 py-0.5 flex-shrink-0">⚠ localhost</span>
                  )}
                  {galleryUrl ? (
                    <>
                      <input readOnly value={galleryUrl} className="flex-1 min-w-0 text-[9px] bg-white border border-indigo-200 rounded px-1.5 py-0.5 text-slate-700 outline-none cursor-text" style={{ minWidth: 100 }} onClick={e => e.target.select()} />
                      <button onClick={() => { copyText(galleryUrl); setCopied('g'); setTimeout(() => setCopied(''), 2000); }} className={`text-[9px] font-bold px-1.5 py-0.5 rounded border flex-shrink-0 ${copied === 'g' ? 'bg-green-600 text-white' : 'bg-white text-slate-600 border-slate-200'}`}>{copied === 'g' ? '✓' : '📋'}</button>
                      <button onClick={() => sendWA('gallery', selected.mobile_number, buildGalleryMessage({ visitorName: visitorDisplayName, eventName: selectedEvent?.name, galleryUrl }))} disabled={waSending === 'gallery'} className={`text-[9px] font-bold px-1.5 py-0.5 rounded border flex-shrink-0 ${waSending === 'gallery' ? 'bg-green-200' : waResult === 'ok' ? 'bg-green-600 text-white border-green-600' : 'bg-green-50 text-green-700 border-green-200'}`}>{waSending === 'gallery' ? '…' : waResult === 'ok' ? '✓ Sent' : '📤 WA'}</button>
                      <a href={`https://wa.me/?text=${encodeURIComponent(buildGalleryMessage({ visitorName: visitorDisplayName, eventName: selectedEvent?.name, galleryUrl }))}`} target="_blank" rel="noreferrer" className="text-[9px] font-bold px-1.5 py-0.5 rounded border bg-white text-slate-500 border-slate-200 flex-shrink-0">📱</a>
                    </>
                  ) : (
                    <button onClick={generateGalleryLink} disabled={generatingLink || !selected.eum_id} className="text-[9px] font-bold px-2 py-0.5 rounded border bg-white text-indigo-600 border-indigo-300 hover:bg-indigo-50 disabled:opacity-50">{generatingLink ? '…' : '+ Generate Link'}</button>
                  )}
                  <span className="text-slate-300 flex-shrink-0">|</span>
                  <span className="text-[9px] font-bold text-slate-500 flex-shrink-0">Event URL:</span>
                  {eventRegisterUrl && (
                    <>
                      <button onClick={() => { copyText(eventRegisterUrl); setCopied('e'); setTimeout(() => setCopied(''), 2000); }} className={`text-[9px] font-bold px-1.5 py-0.5 rounded border flex-shrink-0 ${copied === 'e' ? 'bg-green-600 text-white' : 'bg-white text-slate-600 border-slate-200'}`}>{copied === 'e' ? '✓' : '📋'}</button>
                      <a href={`https://wa.me/?text=${encodeURIComponent(buildEventInviteMessage({ eventName: selectedEvent?.name, registerUrl: eventRegisterUrl }))}`} target="_blank" rel="noreferrer" className="text-[9px] font-bold px-1.5 py-0.5 rounded border bg-green-50 text-green-700 border-green-200 flex-shrink-0">📱</a>
                    </>
                  )}
                </div>

                {/* ── PHOTOS VIEW ── */}
                {rightView === 'photos' && (
                  <div className="flex-1 overflow-y-auto p-2">
                    {loadingPhotos && <div className="flex items-center gap-2 text-[10px] text-slate-400 mb-2"><div className="w-3 h-3 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin" />Loading…</div>}
                    {!loadingPhotos && photosError && (
                      <div className="text-[10px] text-red-700 bg-red-50 border border-red-200 rounded-lg p-2 mb-2">❌ {photosError}</div>
                    )}
                    {!loadingPhotos && !photosError && photos.length === 0 && (
                      <div className="text-center py-6"><div className="text-2xl mb-1">🔍</div><p className="text-[10px] text-slate-500">No matched photos yet.</p></div>
                    )}
                    {!loadingPhotos && photos.length > 0 && (
                      <div className="grid grid-cols-5 gap-1.5">
                        {photos.map(p => (
                          <div key={p.id} onClick={() => setLightbox(p)} className="cursor-pointer rounded-lg overflow-hidden border border-slate-200 hover:border-indigo-400 transition-all">
                            <img src={p.photoUrl} alt="" className="w-full aspect-square object-cover" onError={e => { e.target.style.display = 'none'; }} />
                            <div className="text-center text-[9px] text-slate-400 py-0.5">{Math.round((p.confidence_score || 0) * 100)}%</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* ── EDIT VIEW ── */}
                {rightView === 'edit' && selFormState && (
                  <div className="flex-1 overflow-y-auto p-2">
                    {/* Selfie upload */}
                    <div className="bg-blue-50 rounded-lg border border-blue-200 p-2 mb-2">
                      <p className="text-[9px] font-bold text-blue-700 mb-1">📷 Replace Selfie</p>
                      <div className="flex items-center gap-2">
                        <SelfieOrAvatar userId={selected.id} name={visitorDisplayName} colorClass="bg-blue-700" size="w-10 h-10" bust={selfieBust[selected.id]} />
                        <input type="file" accept="image/*" onChange={e => setSelfieFile(e.target.files[0] || null)} className="text-[9px]" />
                        <button onClick={uploadSelfie} disabled={!selfieFile || uploadingSelfie} className="text-[9px] font-bold bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white px-2 py-1 rounded-lg">{uploadingSelfie ? '…' : 'Upload'}</button>
                      </div>
                      {selfieMsg && <p className="text-[9px] text-blue-700 mt-1">{selfieMsg}</p>}
                    </div>

                    {/* Form grid */}
                    <div className="grid grid-cols-4 gap-x-2 gap-y-1">
                      {FIELDS.map(([label, key, type = 'text']) => (
                        <div key={key} className="flex flex-col gap-0.5">
                          <label className="text-[8px] font-bold text-slate-400 uppercase">{label}</label>
                          <input
                            className="border border-slate-200 rounded px-1.5 py-0.5 text-[10px] outline-none focus:border-indigo-400 w-full"
                            type={type}
                            placeholder={key === 'password' ? 'keep blank' : ''}
                            value={selFormState[key] || ''}
                            onChange={e => setSelForm(p => p ? ({ ...p, [key]: e.target.value }) : p)}
                          />
                        </div>
                      ))}
                    </div>
                    <div className="flex items-center justify-between mt-2">
                      <label className="flex items-center gap-1 text-[10px] font-semibold text-slate-600 cursor-pointer">
                        <input type="checkbox" checked={!!selFormState.is_active} onChange={e => setSelForm(p => p ? ({ ...p, is_active: e.target.checked }) : p)} className="w-3 h-3" />
                        Active
                      </label>
                      <div className="flex items-center gap-2">
                        {visitorSaveMsg && <span className={`text-[9px] font-semibold ${visitorSaveMsg.includes('uccess') ? 'text-green-700' : 'text-red-700'}`}>{visitorSaveMsg}</span>}
                        <button onClick={saveVisitor} disabled={visitorSaving} className="text-[10px] font-bold bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white px-3 py-1 rounded-lg">{visitorSaving ? '…' : '💾 Save'}</button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Event Details Modal ── */}
      {showEventModal && eventId && selectedEvent && (
        <EventDetailsModal
          event={selectedEvent}
          onClose={() => setShowEventModal(false)}
          onUpdate={(updated) => {
            setEvents(prev => prev.map(e => e.id === updated.id ? { ...e, ...updated } : e));
            setShowEventModal(false);
          }}
        />
      )}

      {/* Lightbox */}
      {lightbox && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center cursor-zoom-out" onClick={() => setLightbox(null)}>
          <img src={lightbox.photoUrl} alt="" className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg" onClick={e => e.stopPropagation()} />
          <button className="absolute top-4 right-4 text-white text-3xl font-bold hover:text-red-400" onClick={() => setLightbox(null)}>✕</button>
        </div>
      )}
    </div>
  );
};

// ── Share Event Panel ──────────────────────────────────────────────────────────
const ShareEventPanel = ({ eventId, eventRegisterUrl, headers }) => {
  const [mobileInput, setMobileInput] = useState('');
  const [mobileList, setMobileList]   = useState([]);
  const [sendingLink, setSendingLink] = useState(false);
  const [sendingOtp,  setSendingOtp]  = useState(false);
  const [result,      setResult]      = useState({}); // { ok: [], fail: [] }
  const [copied, setCopied] = useState(false);

  const addMobile = (raw) => {
    const all = (raw || mobileInput)
      .split(/[,\n]+/)
      .map(s => s.replace(/\D/g, '').trim())
      .filter(s => s.length === 10);
    if (all.length > 0) setMobileList(prev => [...new Set([...prev, ...all])]);
    setMobileInput('');
  };

  const removeMobile = (m) => setMobileList(prev => prev.filter(x => x !== m));

  const sendAll = async (type) => {
    if (mobileList.length === 0) return;
    const setSending = type === 'link' ? setSendingLink : setSendingOtp;
    setResult({}); setSending(true);
    const ok = [], fail = [];
    for (const m of mobileList) {
      try {
        if (type === 'link') {
          await axios.post(`${API}/events/${eventId}/send-invite`, { mobile_number: m }, { headers });
        } else {
          await axios.post(`${API}/events/${eventId}/send-otp-invite`, { mobile_number: m }, { headers });
        }
        ok.push(m);
      } catch { fail.push(m); }
    }
    setResult({ ok, fail }); setSending(false);
  };

  return (
    <div className="p-3 space-y-3">
      {/* URL */}
      {eventRegisterUrl && (
        <div className="bg-slate-50 rounded-xl border border-slate-200 p-3">
          <p className="text-[10px] font-bold text-slate-700 mb-1.5">🔗 Registration URL</p>
          <div className="flex gap-1.5">
            <input readOnly value={eventRegisterUrl} className="flex-1 text-[9px] bg-white border border-slate-200 rounded-lg px-2 py-1 text-slate-600 font-mono outline-none" onClick={e => e.target.select()} />
            <button onClick={() => { navigator.clipboard?.writeText(eventRegisterUrl); setCopied(true); setTimeout(() => setCopied(false), 2000); }} className={`text-[9px] font-bold px-2 py-1 rounded-lg border flex-shrink-0 ${copied ? 'bg-green-600 text-white border-green-600' : 'bg-white text-slate-600 border-slate-200'}`}>{copied ? '✓' : '📋'}</button>
            <a href={`https://wa.me/?text=${encodeURIComponent('📸 Register here for event photos: ' + eventRegisterUrl)}`} target="_blank" rel="noreferrer" className="text-[9px] font-bold px-2 py-1 rounded-lg border bg-green-50 text-green-700 border-green-200 flex-shrink-0">📱</a>
          </div>
        </div>
      )}

      {/* Mobile input */}
      <div className="bg-indigo-50 rounded-xl border border-indigo-200 p-3">
        <p className="text-[10px] font-bold text-indigo-700 mb-2">📱 Add Mobile Numbers</p>
        <div className="flex gap-1.5 mb-2">
          <input
            className="flex-1 text-xs border border-indigo-200 rounded-lg px-2 py-1.5 outline-none focus:border-indigo-400"
            placeholder="Enter 10-digit number, press Add"
            value={mobileInput}
            onChange={e => setMobileInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addMobile(mobileInput); } }}
          />
          <button onClick={() => addMobile(mobileInput)} className="text-[10px] font-bold bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1.5 rounded-lg border border-indigo-600 flex-shrink-0">Add</button>
        </div>
        {mobileList.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-2">
            {mobileList.map(m => (
              <span key={m} className="inline-flex items-center gap-1 bg-indigo-100 border border-indigo-300 text-indigo-700 text-[9px] font-bold px-2 py-0.5 rounded-full">
                +91 {m} <button onClick={() => removeMobile(m)} className="text-indigo-400 hover:text-red-500 font-bold ml-0.5">✕</button>
              </span>
            ))}
          </div>
        )}
        <div className="flex gap-1.5">
          <button onClick={() => sendAll('link')} disabled={mobileList.length === 0 || sendingLink} className={`flex-1 text-[10px] font-bold py-1.5 rounded-lg border transition-colors ${mobileList.length === 0 || sendingLink ? 'opacity-50' : 'bg-indigo-600 text-white border-indigo-600 hover:bg-indigo-700'}`}>
            {sendingLink ? '⏳ Sending…' : '📤 Send Link'}
          </button>
          <button onClick={() => sendAll('otp')} disabled={mobileList.length === 0 || sendingOtp} className={`flex-1 text-[10px] font-bold py-1.5 rounded-lg border transition-colors ${mobileList.length === 0 || sendingOtp ? 'opacity-50' : 'bg-purple-600 text-white border-purple-600 hover:bg-purple-700'}`}>
            {sendingOtp ? '⏳ Sending…' : '🔐 Send OTP'}
          </button>
        </div>
        {result.ok?.length > 0 && (
          <p className="text-[9px] text-green-700 mt-1.5 font-semibold">✓ Sent to {result.ok.length} number{result.ok.length > 1 ? 's' : ''}{result.fail?.length > 0 ? `, ${result.fail.length} failed` : ''}</p>
        )}
        {result.fail?.length > 0 && (
          <p className="text-[9px] text-red-600 mt-1 font-semibold">✗ Failed: {result.fail.join(', ')}</p>
        )}
      </div>

      {/* From visitor list */}
      <div className="bg-slate-50 rounded-xl border border-slate-200 p-3">
        <p className="text-[10px] font-bold text-slate-600 mb-1.5">👥 From Visitor List</p>
        <p className="text-[9px] text-slate-400">All registered visitor mobiles are available below — use the Share button next to each visitor.</p>
      </div>

      {/* Flow */}
      <div className="bg-amber-50 rounded-xl border border-amber-200 p-3">
        <p className="text-[10px] font-bold text-amber-700 mb-1.5">📋 Self-Registration Flow</p>
        <ol className="text-[9px] text-amber-800 space-y-0.5 list-decimal list-inside">
          <li>Guest opens registration link</li>
          <li>Enters mobile + OTP verification</li>
          <li>Sets password (sent to WhatsApp)</li>
          <li>Uploads clear selfie photo</li>
          <li>System generates gallery link</li>
          <li>Gallery shared on WhatsApp</li>
        </ol>
      </div>
    </div>
  );
};

// ── Event-level right panel ─────────────────────────────────────────────────────
const EventLevelPanel = ({ eventId, eventName, visitorCount, headers, eventRegisterUrl }) => {
  const [rightTab, setRightTab] = useState('overview'); // 'overview' | 'noidentity' | 'share'
  const [noFacePhotos, setNoFacePhotos] = useState([]);
  const [loadingNoFace, setLoadingNoFace] = useState(false);
  const [copied, setCopied] = useState('');
  const [lightbox, setLightbox] = useState(null);

  const loadNoFace = useCallback(async () => {
    setLoadingNoFace(true);
    try {
      const { data } = await axios.get(`${API}/events/${eventId}/photos?category=no_face&limit=200`, { headers });
      const list = (data.photos || []).map(p => ({
        ...p,
        photoUrl: PHOTO_BASE ? `${PHOTO_BASE}/${eventId}/${lowerExt(p.file_name)}` : `/api/photos/file/${p.id}`,
      }));
      setNoFacePhotos(list);
    } catch { setNoFacePhotos([]); }
    setLoadingNoFace(false);
  }, [eventId, headers]);

  useEffect(() => {
    if (rightTab === 'noidentity') loadNoFace();
  }, [rightTab, eventId, loadNoFace]);

  return (
    <div className="flex-1 bg-white rounded-xl border border-slate-200 flex flex-col overflow-hidden min-w-0">
      {/* Tab bar */}
      <div className="flex items-center gap-1 px-3 py-1.5 border-b border-slate-100 bg-slate-50 flex-shrink-0">
        {[
          { key: 'overview',   label: '📊 Overview' },
          { key: 'noidentity', label: '❓ No Identity', badge: noFacePhotos.length || '' },
          { key: 'share',      label: '📤 Share Event' },
        ].map(t => (
          <button
            key={t.key}
            onClick={() => setRightTab(t.key)}
            className={`text-[10px] font-bold px-2.5 py-1 rounded-lg transition-colors ${rightTab === t.key ? 'bg-indigo-600 text-white' : 'text-slate-500 hover:bg-slate-100'}`}
          >
            {t.label}{t.badge ? ` (${t.badge})` : ''}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">

        {/* Overview */}
        {rightTab === 'overview' && (
          <div className="p-3 space-y-3">
            <div className="text-center">
              <div className="text-4xl mb-1">🎉</div>
              <div className="font-bold text-sm text-slate-800">{eventName}</div>
              <div className="text-[10px] text-slate-400 mt-0.5">Event Overview</div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {[
                { label: 'Total Visitors', value: visitorCount, icon: '👥', color: 'bg-blue-50 border-blue-200 text-blue-700' },
                { label: 'Registered', value: visitorCount, icon: '✓', color: 'bg-green-50 border-green-200 text-green-700' },
              ].map(s => (
                <div key={s.label} className={`rounded-xl border p-2.5 text-center ${s.color}`}>
                  <div className="text-xl font-extrabold">{s.value}</div>
                  <div className="text-[9px] font-semibold mt-0.5">{s.label}</div>
                </div>
              ))}
            </div>
            <div className="bg-indigo-50 rounded-xl border border-indigo-200 p-3">
              <p className="text-[10px] font-bold text-indigo-700 mb-1.5">🔗 Registration URL</p>
              {eventRegisterUrl && (
                <div className="flex gap-1.5">
                  <input readOnly value={eventRegisterUrl} className="flex-1 text-[9px] bg-white border border-indigo-200 rounded-lg px-2 py-1 text-slate-600 font-mono outline-none" onClick={e => e.target.select()} />
                  <button
                    onClick={() => { navigator.clipboard?.writeText(eventRegisterUrl); setCopied('ev'); setTimeout(() => setCopied(''), 2000); }}
                    className={`text-[9px] font-bold px-2 py-1 rounded-lg border flex-shrink-0 ${copied === 'ev' ? 'bg-green-600 text-white border-green-600' : 'bg-white text-slate-600 border-slate-200'}`}
                  >{copied === 'ev' ? '✓' : '📋'}</button>
                </div>
              )}
              <p className="text-[9px] text-indigo-500 mt-1">Share this link with guests to register and upload their selfie.</p>
            </div>
            <div className="text-center text-[9px] text-slate-400">Select a visitor from the list to view their photos</div>
          </div>
        )}

        {/* No Identity */}
        {rightTab === 'noidentity' && (
          <div className="p-2">
            <p className="text-[10px] text-slate-500 mb-2 px-1">Photos with no detected faces — cannot be matched to any visitor.</p>
            {loadingNoFace ? (
              <div className="text-center py-6 text-[10px] text-slate-400">Loading…</div>
            ) : noFacePhotos.length === 0 ? (
              <div className="text-center py-6"><div className="text-2xl mb-1">🎉</div><div className="text-[10px] text-slate-500">No un-identified photos!</div></div>
            ) : (
              <div className="grid grid-cols-6 gap-1.5">
                {noFacePhotos.map(p => (
                  <div key={p.id} onClick={() => setLightbox(p)} className="cursor-pointer rounded-lg overflow-hidden border border-slate-200 hover:border-amber-400 transition-all">
                    <img src={p.photoUrl} alt="" className="w-full aspect-square object-cover" onError={e => { e.target.style.display = 'none'; }} />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Share Event */}
        {rightTab === 'share' && (
          <ShareEventPanel
            eventId={eventId}
            eventRegisterUrl={eventRegisterUrl}
            headers={headers}
          />
        )}
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center cursor-zoom-out" onClick={() => setLightbox(null)}>
          <img src={lightbox.photoUrl} alt="" className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg" onClick={e => e.stopPropagation()} />
          <button className="absolute top-4 right-4 text-white text-3xl font-bold hover:text-red-400" onClick={() => setLightbox(null)}>✕</button>
        </div>
      )}
    </div>
  );
};

// ── Event Details Modal ──────────────────────────────────────────────────────────
const EventDetailsModal = ({ event, onClose, onUpdate }) => {
  const [form, setForm] = useState({
    name: event.name || event.Name || '',
    date: event.date || event.Date || '',
    venue: event.venue || event.Venue || '',
    description: event.description || event.Description || '',
    status: event.status || event.Status || 'completed',
  });
  const [saving, setSaving] = useState(false);
  const [regenMsg, setRegenMsg] = useState('');
  const [regenLoading, setRegenLoading] = useState(false);
  const [msg, setMsg] = useState('');
  const token = localStorage.getItem('ep_token');

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true); setMsg('');
    try {
      await axios.put(`/api/admin/events/${event.id}`, form, { headers: { Authorization: `Bearer ${token}` } });
      setMsg('✓ Saved successfully.');
      setTimeout(() => onUpdate({ ...event, ...form }), 500);
    } catch (err) { setMsg(err.response?.data?.error || 'Save failed.'); }
    setSaving(false);
  };

  const regenerateQR = async () => {
    setRegenLoading(true); setRegenMsg('');
    try {
      const { data } = await axios.post(`/api/superadmin/events/${event.id}/regenerate-qr`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setRegenMsg('✓ QR regenerated with private URL!');
      setTimeout(() => onUpdate({ ...event, register_token: data.register_url?.split('token=')[1] }), 1000);
    } catch { setRegenMsg('✗ Failed to regenerate QR.'); }
    setRegenLoading(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-96 max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-200 bg-slate-50 rounded-t-2xl">
          <div className="font-bold text-sm text-slate-800">✏️ Event Details</div>
          <button onClick={onClose} className="text-slate-400 hover:text-red-500 text-xl font-bold">✕</button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {[
            ['Event Name', 'name', 'text'],
            ['Date', 'date', 'date'],
            ['Venue', 'venue', 'text'],
            ['Description', 'description', 'text'],
            ['Status', 'status', 'select'],
          ].map(([label, key, type]) => (
            <div key={key} className="flex flex-col gap-0.5">
              <label className="text-[10px] font-bold text-slate-500 uppercase">{label}</label>
              {type === 'select' ? (
                <select className="border border-slate-200 rounded px-2 py-1 text-xs outline-none focus:border-indigo-400 w-full" value={form[key]} onChange={e => f(key, e.target.value)}>
                  {['pending','active','completed','cancelled'].map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              ) : (
                <input className="border border-slate-200 rounded px-2 py-1 text-xs outline-none focus:border-indigo-400 w-full" type={type} value={form[key] || ''} onChange={e => f(key, e.target.value)} />
              )}
            </div>
          ))}

          {/* QR Regenerate */}
          <div className="bg-amber-50 rounded-xl border border-amber-200 p-3">
            <p className="text-[10px] font-bold text-amber-700 mb-1">📱 QR Code</p>
            <button
              onClick={regenerateQR}
              disabled={regenLoading}
              className="text-[10px] font-bold bg-amber-600 hover:bg-amber-700 disabled:opacity-60 text-white px-3 py-1.5 rounded-lg w-full"
            >
              {regenLoading ? '⏳ Generating…' : '🔄 Regenerate QR (Private URL)'}
            </button>
            {regenMsg && <p className={`text-[10px] font-semibold mt-1 ${regenMsg.includes('✓') ? 'text-green-700' : 'text-red-600'}`}>{regenMsg}</p>}
            <p className="text-[9px] text-amber-600 mt-1">Creates a secure token-based URL that cannot be guessed.</p>
          </div>

          {msg && <p className={`text-xs font-semibold ${msg.includes('✓') ? 'text-green-700' : 'text-red-700'}`}>{msg}</p>}
        </div>
        <div className="px-4 py-2.5 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onClose} className="text-xs font-semibold px-4 py-1.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50">Cancel</button>
          <button onClick={save} disabled={saving} className="text-xs font-bold bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white px-4 py-1.5 rounded-lg">{saving ? 'Saving…' : '💾 Save'}</button>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminEventOwnersPage;
